// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { TimelineOutput, TimelineEvent, CaseOutput, OrgPermissionsEnum } from 'thehive-sdk';
import { useParams } from 'react-router-dom';
import { useLocalMessage } from '@Helpers/locale';
import { THLayout } from '@Molecules/THLayout';
import client from '@Client';
import { useGenericQuery } from '@Queries/genericQueries';
import { IdType, Timeline as VisTimeline, TimelineGroup, TimelineOptions } from 'vis-timeline/standalone';
import { generateId } from '@Helpers/string';
import { DisplayEvent } from '@Organisms/Timeline/DisplayEvent';
import TimelineDrawer from '@Organisms/Timeline/TimelineDrawer/TimelineDrawer';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { openMessage, handleNotificationError } from '@Helpers/notification';
import { TimelineEventList } from '@Organisms/Timeline/eventList';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { Space, Timeline } from 'antd';
import { classList } from '@Helpers/className';
import { useStorageTimeline } from '@Hooks/useStorageTimeline';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import { formatObservableTitle } from '@Helpers/observable';
import { exportToJSON } from '@Helpers/download';
import useOneQuery from '@Hooks/useOneQuery';
import { FormatDate } from '@Molecules/Format';
import { THRadio } from '@Molecules/Inputs/THRadio/THRadio';
import { THDropdown } from '@Molecules/Inputs/THDropdown/THDropdown';
import { THCheckbox } from '@Molecules/Inputs/THCheckbox/THCheckbox';
import useCasePermissions from '@Hooks/useCasePermissions';
import { THMenu } from '@Molecules/THMenu/THMenu';
import { ICONS } from '@Constants/iconsApp';
import { StatusContext } from '@Context/StatusProvider';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { UnavailableMessage } from '@Molecules/UnavailableMessage/UnavailableMessage';

import './Timeline.scss';
import { useDrawer } from '@Hooks/useDrawer';

export interface TimelineItemType {
    start: number;
    group: string;
    id: string;
    className: string;
    content: string | true;
    date: number;
    endDate?: number | undefined;
    kind: string;
    entity: string;
    entityId: string;
    details?: Record<string, any> | undefined;
}

interface TimelineGroupCustom extends TimelineGroup {
    key: string;
}

const iconGroup: Record<string, ReactElement> = {
    customevent: <ICONS.TimelineEventIcon />,
    procedure: <ICONS.TTPIcon />,
    observable: <ICONS.ObservableIcon />,
    log: <ICONS.TaskLogIcon />,
    task: <ICONS.TaskIcon />,
    alert: <ICONS.AlertIcon />,
    case: <ICONS.CaseIcon />,
};

const forceGroupOrder = ['customevent', 'procedure', 'observable', 'log', 'task', 'alert', 'case'];
const groupOrder = (a: any, b: any) => {
    return forceGroupOrder.indexOf(a.key) - forceGroupOrder.indexOf(b.key);
};
const optionsDefault: TimelineOptions = {
    height: '100%',
    zoomMax: 94672800000, // 3 years
    zoomMin: 3600000, // 1 hour
    groupOrder,
    showCurrentTime: true,
    verticalScroll: true,
    horizontalScroll: true,
    zoomKey: 'ctrlKey',
};

function computeContent(curr: TimelineEvent): string | boolean {
    if (curr.entity == 'Case') return false;
    if (curr.entity == 'Procedure')
        return `[${curr.details.procedure.tacticLabel}] - ${curr.details.procedure.patternName}`;
    if (curr.entity == 'Log') return `${curr.details.log.message}`;
    if (curr.entity == 'Alert') return `${curr.details.alert.title}`;
    if (curr.entity == 'Observable')
        return `<span>[${curr.details.observable.dataType}]</span><br/> ${formatObservableTitle({
            data: curr.details.observable.data,
            attachment: curr.details.observable.attachment,
        })}`;
    if (curr.entity == 'Task') return `${curr.details.task.title}`;
    if (curr.entity == 'CustomEvent') return `${curr.details.customEvent.title}`;
    return false;
}

const CaseTimeline = (): ReactElement => {
    const local = useLocalMessage();

    const { licenseCapabilities } = useContext(StatusContext);

    const timelineCapabilities = useMemo(() => !!licenseCapabilities?.case?.timeline || false, [licenseCapabilities]);

    const { timelineStorage, setTimelineStorage } = useStorageTimeline();
    const timelineDom = useRef<HTMLDivElement>(null);
    const [timelineInstance, setTimelineInstance] = useState<VisTimeline | null>(null);
    const [treatData, setTreatData] = useState<TimelineItemType[]>([]);

    const [focusEventId, setFocusEventId] = useState<string | null>(null);
    const [focusEventTimeline, setFocusEventTimeline] = useState<string | null>(null);

    const [options] = useState<TimelineOptions>(optionsDefault);
    const [groups, setGroups] = useState<TimelineGroupCustom[]>([]);
    const [currentEvent, setCurrentEvent] = useState<TimelineItemType | null>(null);

    const [visible, setVisible] = useState<boolean>(false);
    const [modeList, setModeList] = useState<boolean>(false);

    const [showCurrentTime, setShowCurrentTime] = useState<boolean>(true);

    const { visible: drawerVisible, setVisible: setDrawerVisible } = useDrawer({});

    const { caseId } = useParams<{ caseId: string }>();

    const { data: caseData } = useOneQuery<CaseOutput>({
        selector: { head: [{ name: 'getCase', idOrName: caseId }], name: `get-case-${caseId}` },
        controller: client.CaseController,
        manual: true,
        fetchOnStart: false,
    });

    const hasPermissions = useCasePermissions({ caseId, permissionsNeeded: [OrgPermissionsEnum.MANAGE_CUSTOM_EVENT] });

    const { number } = caseData || {};

    const { data, refetch } = useGenericQuery<TimelineOutput>({
        fetchCallback: () => client.TimelineController.get(caseId),
        key: ['get-timeline-case', caseId],
        manual: true,
        fetchOnStart: false,
    });

    const handleVisibleChange = (flag: any) => {
        setVisible(flag);
    };

    const createItem: (curr: TimelineEvent) => TimelineItemType = useCallback(
        (curr: TimelineEvent) => {
            let opt = {};
            const entity = curr.entity.toLowerCase();
            const item: TimelineItemType = {
                ...curr,
                start: curr.date,
                group: entity,
                id: generateId(),
                className: `timeline__item entity__${entity}`,
                content: computeContent(curr) || local(`timeline.kind.${curr.kind}`),
            };

            if (curr.kind.startsWith('case') || curr.kind.startsWith('alert')) {
                item.className = classList(item.className, curr.kind.replace('.', '-'));
            }

            if (curr.endDate) {
                opt = { end: curr.endDate };
            } else if (entity === 'task') {
                opt = { end: Math.floor(Date.now()), className: classList(item.className, 'unfinished') };
            } else {
                opt = { type: 'box' };
            }

            return {
                ...item,
                ...opt,
            };
        },
        [local],
    );

    const onSwitchHandler = useCallback(
        (id: IdType) => (value: boolean) => {
            setGroups((st) => st.map((group) => (group.id === id ? { ...group, visible: value } : group)));
            setTimelineStorage((st) => ({
                ...st,
                groups: {
                    ...(st.groups || {}),
                    [id]: value,
                },
            }));
        },
        [setGroups, setTimelineStorage],
    );

    const onClickHandler = useCallback(
        (properties) => {
            const event = treatData.find(({ id }) => id === properties.item);
            setCurrentEvent(event || null);
        },
        [treatData, setCurrentEvent], //eslint-disable-line
    );

    useEffect(() => {
        if (treatData && currentEvent) {
            const event = treatData.find(({ entityId }) => entityId === currentEvent.entityId);

            setCurrentEvent(event || null);
        }
    }, [treatData]); // eslint-disable-line

    const eventList = useMemo(
        () =>
            TimelineEventList({
                controller: client.TimelineController,
                onSuccess: (message, action) => {
                    if (message) {
                        openMessage('success', message);
                        if (action === 'delete') {
                            setCurrentEvent(null);
                        }
                        refetch();
                    }
                },
                onError: handleNotificationError,
                local,
            }),
        [local, refetch], //eslint-disable-line
    );

    const handlerExport = useCallback(() => {
        if (treatData) {
            const cleanData = treatData.map((event) => {
                let content = event.content;
                let optDate = {};

                if (content && typeof content === 'string') {
                    content = content.replace(/<\/?[^>]+(>|$)/g, '');
                }

                if (event.endDate && !event.className.includes('unfinished')) {
                    optDate = {
                        endDate: event.endDate,
                    };
                }

                return {
                    entity: event.entity,
                    entityId: event.entityId,
                    content,
                    date: event.date,
                    details: event.details || {},
                    ...optDate,
                };
            });

            exportToJSON(`timeline-case-${number}`, cleanData);
        }
    }, [treatData, number]); // eslint-disable-line

    const onSubmitHandler = useCallback(
        (value) => {
            eventList.onCreate(value, caseId).then((data) => {
                refetch();
                if (data && !modeList) {
                    setFocusEventId(data._id);
                }
            });
        },
        [eventList, caseId], //eslint-disable-line
    ); //

    const onModeSwitchHandler = useCallback(
        (value: boolean) => {
            setModeList(value);
            setCurrentEvent(null);
        },
        [setModeList],
    );

    const forceZoomTimeline = useCallback(() => {
        if (timelineInstance) {
            timelineInstance.fit();
        }
    }, [timelineInstance]);

    const zoomOutTimeline = useCallback(() => {
        if (timelineInstance) {
            timelineInstance.zoomOut(0.7);
        }
    }, [timelineInstance]);
    const zoomInTimeline = useCallback(() => {
        if (timelineInstance) {
            timelineInstance.zoomIn(0.7);
        }
    }, [timelineInstance]);

    useEffect(() => {
        if (data?.events && data?.events.length) {
            setFocusEventTimeline(null);
            const newData = data.events.reduce((acc, curr) => {
                const timelineEvent = createItem(curr);
                if (curr.entityId === focusEventId) {
                    setFocusEventTimeline(timelineEvent.id);
                }
                return curr.kind.toLocaleLowerCase() !== 'case.new' ? [...acc, timelineEvent] : acc;
            }, [] as TimelineItemType[]);
            setTreatData(newData);
        }
    }, [data]); //eslint-disable-line

    useEffect(() => {
        if (treatData.length && timelineInstance !== null) {
            let tmplistGroupName: string[] = [];
            const groupStorage = timelineStorage.groups || {};
            const groupTreat = treatData
                .reduce<TimelineGroupCustom[]>((acc, curr) => {
                    const groupName = curr.group.toLowerCase();

                    if (tmplistGroupName.includes(groupName)) {
                        return acc;
                    }

                    tmplistGroupName = [...tmplistGroupName, groupName];

                    return [
                        ...acc,
                        {
                            key: groupName,
                            content: local(`timeline.groups.${groupName}`),
                            id: groupName,
                            className: `group__${groupName}`,
                            visible: groupStorage[groupName] !== undefined ? groupStorage[groupName] : true,
                        },
                    ];
                }, [])
                .filter(Boolean);

            setGroups(groupTreat.sort(groupOrder));
            timelineInstance.setItems(treatData as any[]);
            if (focusEventTimeline) {
                setTimeout(() => {
                    timelineInstance.focus(focusEventTimeline);
                    setFocusEventTimeline(null);
                }, 100);
            } else {
                !modeList && timelineInstance.fit();
            }
        }
    }, [treatData, modeList]); //eslint-disable-line

    /** set Option to Timeline  */
    useEffect(() => {
        if (timelineInstance !== null) {
            timelineInstance.setOptions(options);
        }
    }, [options]); //eslint-disable-line

    /** set Group to Timeline  */
    useEffect(() => {
        if (timelineInstance !== null) {
            timelineInstance.setGroups(groups);
        }
    }, [groups]); //eslint-disable-line

    /** set event listener to Timeline  */
    useEffect(() => {
        if (timelineInstance !== null) {
            timelineInstance.setOptions(options);
            timelineInstance.on('click', onClickHandler);
        }
        return () => {
            if (timelineInstance !== null) {
                timelineInstance.off('click', onClickHandler);
            }
        };
    }, [timelineInstance, treatData]); //eslint-disable-line

    useEffect(() => {
        if (timelineDom.current !== null && timelineInstance === null) {
            setTimelineInstance(new VisTimeline(timelineDom.current, [], {}));
        }
        return () => {
            if (timelineInstance !== null) {
                timelineInstance.destroy();
            }
        };
    }, [timelineDom, timelineCapabilities]); //eslint-disable-line

    useEffect(() => {
        refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <THLayout>
            {timelineCapabilities ? (
                <>
                    <THLayout.Content>
                        <div className="timeline__content">
                            <div className="timeline__header">
                                <div className="timeline-header__tool-left">
                                    {hasPermissions && (
                                        <AddButton
                                            onClick={() => setDrawerVisible(true)}
                                            label={local('timeline.action.addCustomEvent')}
                                        />
                                    )}
                                    <THTooltip title={local('timeline.action.exportTimeline')}>
                                        <THButton onClick={handlerExport} type="default" disabled={!hasPermissions}>
                                            <ICONS.ExportFileIcon />
                                        </THButton>
                                    </THTooltip>
                                </div>
                                <div className="timeline-header__tool">
                                    {!modeList && (
                                        <div className="timeline-tool__zoom">
                                            <THButton
                                                type="default"
                                                className={classList('timeline-tool__zoom-btn', 'zoom-in')}
                                                onClick={zoomInTimeline}
                                            >
                                                <ICONS.ZoomInOutlined />
                                            </THButton>
                                            <THButton
                                                type="default"
                                                className={classList('timeline-tool__zoom-btn', 'zoom-out')}
                                                onClick={zoomOutTimeline}
                                            >
                                                <ICONS.ZoomOutOutlined />
                                            </THButton>
                                        </div>
                                    )}

                                    {!modeList && (
                                        <THTooltip title={local('timeline.action.resetTimeline')}>
                                            <THButton type="default" onClick={() => forceZoomTimeline()}>
                                                <ICONS.CenterIcon />
                                            </THButton>
                                        </THTooltip>
                                    )}

                                    <THRadio.Group
                                        value={modeList}
                                        onChange={(e) => onModeSwitchHandler(e.target.value)}
                                    >
                                        <THRadio.Button value={false}>
                                            <Space>
                                                <ICONS.TimelineGraphIcon />
                                                {local('common.graph')}
                                            </Space>
                                        </THRadio.Button>
                                        <THRadio.Button value={true}>
                                            <Space>
                                                <ICONS.TimelineListIcon />
                                                {local('common.list')}
                                            </Space>
                                        </THRadio.Button>
                                    </THRadio.Group>

                                    <THDropdown
                                        quitOnMenuItemClick={false}
                                        triggerElem={() => (
                                            <THButton type="default" className="display-visible__cta">
                                                <ICONS.SettingIcon />
                                            </THButton>
                                        )}
                                        placement="bottomRight"
                                        dropdownClassName="display-visible__wrapper"
                                        onVisibleChange={handleVisibleChange}
                                        trigger={['click']}
                                        visible={visible}
                                    >
                                        {groups.map(({ id, visible, key }) => (
                                            <THMenu.Item key={key} className="display-visible__item" modeReverse>
                                                <span className="display-visible__menu-label">
                                                    {local(`timeline.groups.${key}`)}
                                                </span>
                                                <THCheckbox
                                                    defaultChecked={visible || false}
                                                    onChange={(e) => onSwitchHandler(id)(e.target.checked)}
                                                />
                                            </THMenu.Item>
                                        ))}
                                        <THMenu.Item key={'current-time'} className="display-visible__item" modeReverse>
                                            <span className="display-visible__menu-label">
                                                {local(`timeline.tools.current-time`)}
                                            </span>
                                            <THCheckbox
                                                defaultChecked={showCurrentTime || false}
                                                onChange={(e) => setShowCurrentTime(e.target.checked)}
                                            />
                                        </THMenu.Item>
                                    </THDropdown>
                                </div>
                            </div>

                            <div
                                className={classList('timeline__wrap', {
                                    ['hide-current-time']: !showCurrentTime,
                                })}
                            >
                                {modeList && (
                                    <Timeline mode={'left'} className="timeline__list">
                                        {treatData.map((event) =>
                                            timelineStorage?.groups?.[event.group] !== false ? (
                                                <Timeline.Item
                                                    key={event.id}
                                                    className={classList(event.className, `group__${event.group}`)}
                                                    label={
                                                        <p
                                                            className="timeline__list-date"
                                                            style={{
                                                                [`--content-kind` as string]: `'${local(
                                                                    event.className.includes('unfinished')
                                                                        ? `timeline.kind.task-progress`
                                                                        : `timeline.kind.${event.kind}`,
                                                                )}'`,
                                                            }}
                                                        >
                                                            <FormatDate date={event.start} />
                                                        </p>
                                                    }
                                                    dot={
                                                        <THTooltip title={local(`timeline.groups.${event.group}`)}>
                                                            {event.className.includes('unfinished') ? (
                                                                <ICONS.ClockCircleOutlined className="timeline__list-icon" />
                                                            ) : (
                                                                React.cloneElement(iconGroup[event.group], {
                                                                    className: 'timeline__list-icon',
                                                                })
                                                            )}
                                                        </THTooltip>
                                                    }
                                                >
                                                    <DisplayEvent
                                                        currentEvent={event}
                                                        eventList={eventList}
                                                        caseData={caseData}
                                                        refetch={refetch}
                                                        hasPermissionEvent={hasPermissions}
                                                    />
                                                </Timeline.Item>
                                            ) : null,
                                        )}
                                    </Timeline>
                                )}

                                <div ref={timelineDom} className={classList('timeline__graph', { hide: modeList })} />
                            </div>
                        </div>
                        <div className={classList('timeline__card', { timeline__card__hidden: !currentEvent })}>
                            {currentEvent && (
                                <DisplayEvent
                                    currentEvent={currentEvent}
                                    eventList={eventList}
                                    caseData={caseData}
                                    refetch={refetch}
                                    hasPermissionEvent={hasPermissions}
                                />
                            )}
                        </div>
                    </THLayout.Content>
                    <TimelineDrawer
                        visible={drawerVisible}
                        onVisibleChange={setDrawerVisible}
                        onSubmit={onSubmitHandler}
                    />
                </>
            ) : (
                <UnavailableMessage type="capability" />
            )}
        </THLayout>
    );
};
export const Route: RouteType = {
    menu: {
        label: 'case.layout.nav-bar.timeline',
        icon: <ICONS.ClockCircleOutlined />,
    },
    path: RouteBuilder.compute('cases.timeline'),
    content: <CaseTimeline />,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.case.timeline' },
};
